/**
 * Health Check Service - Verify all APIs are available before starting
 *
 * Checks:
 * - Avatar API (TTS service)
 * - Face API (enrollment/verification)
 * - OpenAI API (via a test endpoint if available)
 */

import { checkTTSHealth } from './tts'
import { debug, PerfTimer } from '../utils/debug'

// API URLs
const AVATAR_API = 'https://reallyassess-avatar-dev.techtree.workers.dev'
const FACE_API = 'https://reallyassess-face-api-dev.techtree.workers.dev'

export interface HealthCheckResult {
  service: string
  status: 'healthy' | 'unhealthy' | 'degraded' | 'checking'
  latency?: number
  error?: string
}

export interface SystemHealth {
  overall: 'healthy' | 'degraded' | 'unhealthy'
  services: HealthCheckResult[]
  timestamp: number
}

/**
 * Check Avatar API (TTS service) health
 */
async function checkAvatarHealth(): Promise<HealthCheckResult> {
  const timer = new PerfTimer('Avatar API Health Check')

  try {
    const isHealthy = await checkTTSHealth(AVATAR_API)
    const latency = timer.end()

    return {
      service: 'Avatar API (TTS)',
      status: isHealthy ? 'healthy' : 'unhealthy',
      latency,
      error: isHealthy ? undefined : 'Service unavailable',
    }
  } catch (error) {
    const latency = timer.end()
    return {
      service: 'Avatar API (TTS)',
      status: 'unhealthy',
      latency,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

/**
 * Check Face API health
 */
async function checkFaceAPIHealth(): Promise<HealthCheckResult> {
  const timer = new PerfTimer('Face API Health Check')

  try {
    // Simple HEAD or OPTIONS request to check availability
    const response = await fetch(`${FACE_API}/health`, {
      method: 'OPTIONS',
    }).catch(() => null)

    const latency = timer.end()
    const isHealthy = response?.ok ?? false

    return {
      service: 'Face API',
      status: isHealthy ? 'healthy' : 'unhealthy',
      latency,
      error: isHealthy ? undefined : 'Service unavailable',
    }
  } catch (error) {
    const latency = timer.end()
    return {
      service: 'Face API',
      status: 'unhealthy',
      latency,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

/**
 * Check browser capabilities (webcam, audio, etc.)
 */
async function checkBrowserCapabilities(): Promise<HealthCheckResult> {
  const timer = new PerfTimer('Browser Capabilities Check')
  const issues: string[] = []

  try {
    // Check getUserMedia support
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      issues.push('Webcam access not supported')
    }

    // Check Web Speech API support
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      issues.push('Speech recognition not supported')
    }

    // Check Audio support
    if (!window.AudioContext && !(window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext) {
      issues.push('Audio API not supported')
    }

    const latency = timer.end()

    return {
      service: 'Browser Capabilities',
      status: issues.length === 0 ? 'healthy' : 'degraded',
      latency,
      error: issues.length > 0 ? issues.join(', ') : undefined,
    }
  } catch (error) {
    const latency = timer.end()
    return {
      service: 'Browser Capabilities',
      status: 'unhealthy',
      latency,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

/**
 * Run all health checks in parallel
 */
export async function checkSystemHealth(): Promise<SystemHealth> {
  debug.log('[HealthCheck] Starting system health check...')

  // Run all checks in parallel for speed
  const [avatarHealth, faceHealth, browserHealth] = await Promise.all([
    checkAvatarHealth(),
    checkFaceAPIHealth(),
    checkBrowserCapabilities(),
  ])

  const services = [avatarHealth, faceHealth, browserHealth]

  // Determine overall health
  const unhealthyCount = services.filter(s => s.status === 'unhealthy').length
  const degradedCount = services.filter(s => s.status === 'degraded').length

  let overall: 'healthy' | 'degraded' | 'unhealthy'
  if (unhealthyCount > 0) {
    overall = 'unhealthy'
  } else if (degradedCount > 0) {
    overall = 'degraded'
  } else {
    overall = 'healthy'
  }

  const result: SystemHealth = {
    overall,
    services,
    timestamp: Date.now(),
  }

  debug.log('[HealthCheck] System health check complete:', result)

  return result
}

/**
 * Get a human-readable summary of health issues
 */
export function getHealthSummary(health: SystemHealth): string {
  const unhealthy = health.services.filter(s => s.status === 'unhealthy')
  const degraded = health.services.filter(s => s.status === 'degraded')

  if (unhealthy.length === 0 && degraded.length === 0) {
    return 'All systems operational'
  }

  const issues: string[] = []

  if (unhealthy.length > 0) {
    issues.push(`Unavailable: ${unhealthy.map(s => s.service).join(', ')}`)
  }

  if (degraded.length > 0) {
    issues.push(`Limited: ${degraded.map(s => s.service).join(', ')}`)
  }

  return issues.join(' • ')
}

/**
 * Check if system is ready to start assessment
 * Returns true if all critical services are healthy
 */
export function isSystemReady(health: SystemHealth): boolean {
  // Critical services that must be healthy
  const criticalServices = ['Avatar API (TTS)', 'Face API']

  const unhealthyCritical = health.services.filter(
    s => criticalServices.includes(s.service) && s.status === 'unhealthy'
  )

  return unhealthyCritical.length === 0
}
